// src/contexts/ProfileUpdateContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { userApi } from "../api/user";
import { UserProfile } from "../types";
import { useAuthContext } from "./AuthContext";

/**
 * Status object for tracking loading, error, and success states.
 */
type Status = {
  loading: boolean;
  error: string | null;
  success: string | null;
};

/**
 * Context interface for profile update state and actions.
 */
interface Ctx {
  status: Status;
  clearStatus: () => void;
  profileData: UserProfile | null;
  setProfileData: React.Dispatch<React.SetStateAction<UserProfile | null>>;
  recipes: any[]; // TODO: Define a proper type for recipes
  fetchProfileData: () => Promise<void>;
  allDisciplines: { value: string; label: string }[];
  allIndustries: { value: string; label: string }[];
}

/**
 * React context for profile update state.
 */
const ProfileUpdateContext = createContext<Ctx | null>(null);

/**
 * Joins validation errors from API responses into a single string.
 * @param errors - The errors object from the API response.
 * @returns {string | null} - Joined error messages or null.
 */
function joinValidationErrors(errors: unknown): string | null {
  if (!errors || typeof errors !== "object") return null;
  try {
    const lines: string[] = [];
    for (const [field, msgs] of Object.entries(
      errors as Record<string, unknown>
    )) {
      if (Array.isArray(msgs)) for (const m of msgs) lines.push(String(m));
      else if (typeof msgs === "string") lines.push(msgs);
    }
    return lines.length ? lines.join(" • ") : null;
  } catch {
    return null;
  }
}

/**
 * Normalizes API error objects into a readable string.
 * @param err - The error object from the API.
 * @returns {string} - Normalized error message.
 */
function normalizeApiError(err: any): string {
  let status =
    err?.status ??
    err?.statusCode ??
    err?.httpStatus ??
    err?.code ??
    err?.response?.status ??
    undefined;

  let body: any =
    err?.data ??
    err?.body ??
    err?.responseBody ??
    err?.payload ??
    err?.error ??
    err?.response?.data ??
    undefined;

  if (typeof body === "string") {
    try {
      body = JSON.parse(body);
    } catch {
      /* keep as text */
    }
  }

  const fromValidation = joinValidationErrors(body?.errors);
  if (fromValidation)
    return status ? `${fromValidation} (HTTP ${status})` : fromValidation;

  const base = body?.message || body?.error || err?.message || "Request failed";

  return status ? `${base} (HTTP ${status})` : base;
}

/**
 * Provider component for the ProfileUpdateContext.
 * Fetches and provides user profile data, disciplines, industries, and recipes.
 * Handles loading, error, and success states.
 */
export const ProfileUpdateProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { user } = useAuthContext();
  const [status, setStatus] = useState<Status>({
    loading: false,
    error: null,
    success: null,
  });
  const [profileData, setProfileData] = useState<UserProfile | null>(null);
  const [recipes, setRecipes] = useState<any[]>([]);
  const [allDisciplines, setAllDisciplines] = useState<
    { value: string; label: string }[]
  >([]);
  const [allIndustries, setAllIndustries] = useState<
    { value: string; label: string }[]
  >([]);

  /**
   * Clears the status state to its initial values.
   */
  const clearStatus = () =>
    setStatus({ loading: false, error: null, success: null });

  /**
   * Fetches the user's profile data and related dropdown options from the API.
   * Updates the context state accordingly.
   */
  const fetchProfileData = async () => {
    if (!user || !user._id) {
      setStatus({
        loading: false,
        error: "User not authenticated.",
        success: null,
      });
      return;
    }

    setStatus({ loading: true, error: null, success: null });
    try {
      const response = await userApi.getUserDetailsWithMore();
      setProfileData(response.userData);
      setAllDisciplines(response.allDisciplines);
      setAllIndustries(response.allIndustries);
      setStatus({ loading: false, error: null, success: null });
    } catch (err: any) {
      setStatus({
        loading: false,
        error: normalizeApiError(err),
        success: null,
      });
    }
  };

  /**
   * Fetch profile data when the user changes (e.g., on login).
   *
   * Uses user._id instead of user object to avoid unnecessary re-fetches.
   */
  useEffect(() => {
    fetchProfileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);

  const value = {
    status,
    clearStatus,
    profileData,
    setProfileData,
    recipes,
    fetchProfileData,
    allDisciplines,
    allIndustries,
  };

  return (
    <ProfileUpdateContext.Provider value={value}>
      {children}
    </ProfileUpdateContext.Provider>
  );
};

/**
 * Custom hook to access the ProfileUpdateContext.
 * @returns {Ctx} - The context value.
 * @throws If used outside of ProfileUpdateProvider.
 */
export const useProfileUpdateContext = () => {
  const ctx = useContext(ProfileUpdateContext);
  if (!ctx)
    throw new Error(
      "useProfileUpdateContext must be used within ProfileUpdateProvider"
    );
  return ctx;
};
